import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import { Error } from '@mui/icons-material';
import { useAuth } from '../../../authentication/AuthenticationContext';
import { useConfig } from '../../../ConfigContext';
import { GameIds } from '../../my-games/MyGames.model';
import { usePromotion } from '../PromotionContext';
import { RequestError } from '../../../Error.model';
import { myI18n } from '../../../i18n';
import { GameProviderConfig } from '../../../Config.model';

interface Props {
  requiredGameId: GameIds;
  nextStep: () => void;
}

export function GameOwnershipValidation({ requiredGameId, nextStep }: Props): JSX.Element {
  const { t } = useTranslation(['error', 'translation']);
  const { user, getPlayer } = useAuth();
  const config = useConfig();
  const { activate } = usePromotion();

  const [hasGameOwned, setHasGameOwned] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errorCode, setErrorCode] = useState<string>(null);
  const [provider, setProvider] = useState<GameProviderConfig>(null);

  const checkIfOwnedGame = async (providerName: string): Promise<void> => {
    try {
      setIsValidating(true);
      const userProvider = user.accounts.find((account) => account.type === providerName);
      if (userProvider?.gamesOwned.includes(requiredGameId)) {
        setHasGameOwned(true);
        nextStep();
        return;
      }
      await activate(providerName, userProvider.id, requiredGameId);
      await getPlayer();
      nextStep();
    } catch (e) {
      const err = e as RequestError;
      setErrorCode(err.code);
      setHasGameOwned(false);
    } finally {
      setIsValidating(false);
    }
  };

  const openStorePage = (): void => {
    if (provider.name === 'steam') {
      window.open(`https://store.steampowered.com/app/${provider.packageId}`, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    const gameConfig = config?.serverConfig.gamesConfig.find((x) => x.id === requiredGameId);
    if (gameConfig) {
      const searchParams = new URLSearchParams(location.search);
      const providerParam = searchParams.get('provider');
      const currentProvider = gameConfig.providers.find((x) => x.name === providerParam) ?? gameConfig.providers[0];
      setProvider(currentProvider);
      checkIfOwnedGame(currentProvider?.name);
    }
  }, [requiredGameId]);

  return (
    <>
      {isValidating && (
        <>
          <div className='loader' data-cy='validate-game-ownership-loader'>
            <CircularProgress />
          </div>
          <p>
            {t(
              'translation:promotion.game-ownership.validating',
              { requiredGame: t(`translation:games.${requiredGameId}`) },
            )}

          </p>
        </>
      )}
      {!isValidating && !hasGameOwned && (
        <>
          <p className='claim-promotion-instructions'>
            {t(
              'translation:promotion.link-provider.description',
              { requiredGame: t(`translation:games.${requiredGameId}`) },
            )}
          </p>
          <div className='activation-error' data-cy='validate-game-ownership-error'>
            <Error />
            {myI18n.exists(`error:${errorCode}`)
              ? t(`error:${errorCode}`, { requiredGame: t(`translation:games.${requiredGameId}`) })
              : t('error:UnknownError')}
          </div>
          {provider?.packageId
          && (
            <button
              type='submit'
              data-cy='buy-game-btn'
              className='btn btn-primary-game mt-4'
              onClick={openStorePage}
            >
              {t('translation:promotion.game-ownership.buy-game')}
            </button>
          )}
        </>
      )}
    </>
  );
}
